import styled from "@emotion/styled";
import type { FC } from "react";
import React from "react";
import { IconLabValueCursor } from "../../constants/LabIcons";

//#region Styled components
const Range = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Segments = styled.div`
  display: flex;
  gap: 0.25rem;
  position: relative;
`;

const Segment = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: var(--width, 0%);
  min-width: 15%;

  @media screen and (min-width: 768px) {
    min-width: 10%;
  }

  &[data-onlyonelimit="true"] {
    max-width: 50%;
  }
`;

const SegmentMinLimit = styled.p`
  position: relative;
  left: 0;
`;

const SegmentMaxLimit = styled.p`
  position: relative;
  right: -0.125rem;
  transform: translateX(50%);

  &[data-lastsegmentlimit="true"] {
    right: 0;
    transform: none;
  }
`;

const Bar = styled.div`
  height: 6px;
  border-radius: 0.25rem;
  width: 100%;
  background-color: var(--Greys-Fog, #f4f4f4);
  position: relative;
  overflow: hidden;

  &[data-code="LL"],
  &[data-code="HH"] {
    background-color: var(--Primary-criticalRed, #ff7663);

    &::after {
      position: absolute;
      inset: 0;
      content: "";
      background: repeating-linear-gradient(
        -45deg,
        #fffcf3,
        #fffcf3 1.25px,
        rgba(255, 255, 255, 0) 0px,
        rgba(255, 255, 255, 0) 3.5px
      );
      opacity: 0.55;
    }
  }

  &[data-code="LU"],
  &[data-code="HU"] {
    background-color: var(--Primary-SunriseOrange, #ffbd70);
  }

  &[data-code="L"],
  &[data-code="H"] {
    background-color: var(--Primary-AfternoonYellow, #ffe082);
  }

  &[data-code="N"] {
    background-color: var(--Primary-InRange, #6fc696);
  }
`;

const Values = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Cursor = styled.div`
  display: flex;
  position: absolute;
  top: -9px;
  left: calc(var(--percentage-from-left, 0%) - 2px);
`;
//#endregion

export type RangeSegment = {
  id?: string | number;
  code?: string;
  min?: number;
  max?: number;
  atLeastOneLimit?: boolean;
  onlyOneLimit?: boolean;
  widthPercentage?: number;
  cursorPositionPercentageFromLeft?: number;
};

export interface ReferenceRangeBarProps {
  observationValue: number;
  ranges: RangeSegment[];
  normalRangeMin?: number;
  normalRangeMax?: number;
  activeRangeId?: string | number;
}

const GraySegment: FC<{ showCursor: boolean }> = ({ showCursor }) => {
  return (
    <Segment style={{ "--width": "25%" } as React.CSSProperties}>
      <Bar />
      {showCursor && (
        <Cursor
          style={{ "--percentage-from-left": "50%" } as React.CSSProperties}
        >
          <IconLabValueCursor />
        </Cursor>
      )}
    </Segment>
  );
};

const ReferenceRangeBar: FC<ReferenceRangeBarProps> = ({
  observationValue,
  ranges,
  normalRangeMin,
  normalRangeMax,
  activeRangeId
}) => {
  if (!ranges?.length || typeof observationValue !== "number") {
    return null;
  }

  return (
    <Range>
      <Segments>
        {ranges.length === 1 && typeof normalRangeMin === "number" && (
          <GraySegment showCursor={observationValue < normalRangeMin} />
        )}

        {ranges.map((range, i) => {
          const {
            code,
            min,
            max,
            atLeastOneLimit,
            onlyOneLimit,
            widthPercentage,
            cursorPositionPercentageFromLeft
          } = range;

          return (
            <Segment
              key={`observation-range-${range.id ?? i}`}
              data-onlyonelimit={onlyOneLimit}
              style={
                { "--width": `${widthPercentage ?? 0}%` } as React.CSSProperties
              }
            >
              <Bar data-code={code} />

              {atLeastOneLimit && (
                <Values>
                  <SegmentMinLimit className="m0">
                    {i === 0 && min}
                  </SegmentMinLimit>
                  <SegmentMaxLimit
                    className="m0"
                    data-lastsegmentlimit={i === ranges.length - 1}
                  >
                    {max}
                  </SegmentMaxLimit>
                </Values>
              )}

              {range.id && range.id === activeRangeId && (
                <Cursor
                  style={
                    {
                      "--percentage-from-left": `${cursorPositionPercentageFromLeft ?? 0}%`
                    } as React.CSSProperties
                  }
                >
                  <IconLabValueCursor />
                </Cursor>
              )}
            </Segment>
          );
        })}

        {ranges.length === 1 && typeof normalRangeMax === "number" && (
          <GraySegment showCursor={observationValue > normalRangeMax} />
        )}
      </Segments>
    </Range>
  );
};

export default ReferenceRangeBar;
